import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material'
import _ from 'lodash'
import getSubmissionErrors from '../../utils/submissionErrors'
import { useNavigate } from 'react-router-dom'

import { useCreateUser, useEditUser } from '../../hooks/api/users'
import { dealershipBrandOptions } from '../../constants/dealershipBrands'
import { useQueryClient } from 'react-query'
import { AutocompleteElement } from 'react-hook-form-mui'
import CustomerForm from './CustomerForm'

const UserForm = ({ user, refetch }) => {
  const navigate = useNavigate()

  const queryClient = useQueryClient()
  const { mutateAsync: updateUser } = useEditUser()
  const { mutateAsync: createUser } = useCreateUser()

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
  } = useForm({
    defaultValues: {
      email: user?.email,
      phone: user?.phone,
      firstName: user?.firstName,
      lastName: user?.lastName,
      isAdmin: user?.isAdmin || false,
      dealership: user?.dealership?._id || user?.dealership,
      dealershipBrand: user?.dealership?.brand || user?.dealershipBrand,
    },
    criteriaMode: 'all',
  })

  const [dealershipData, setDealershipData] = useState(user?.dealership)

  const password = useRef({})
  password.current = watch('password', '')

  const isAdmin = useRef({})
  isAdmin.current = watch('isAdmin', user?.isAdmin || false)

  const isHTAStaff = useRef({})
  isHTAStaff.current = watch('isHTAStaff', (user?.isAdmin && user?.isHTAStaff) || false)

  const dealership = useRef({})
  dealership.current = user?.isHTAStaff ? null : user?.dealership

  const dealershipBrand = useRef({})
  dealershipBrand.current = watch('dealershipBrand', null)

  const handleChangeDealership = (d) => {
    setDealershipData(d)
    if (d) {
      setValue('dealershipBrand', d?.brand)
    }
  }

  useEffect(() => {
    if (
      !dealershipBrand.current ||
      (dealershipBrand.current === dealershipData?.brand &&
        dealershipData?.brand === user?.dealershipBrand &&
        (dealershipData?.id || dealershipData?._id) === (user?.dealership?._id || user?.dealership))
    ) {
      return
    }
    if (dealershipData?.brand !== dealershipBrand.current) {
      setValue('dealership', null)
    }
  }, [dealershipBrand.current])

  const onSubmit = async (data) => {
    try {
      if (user) {
        await updateUser({
          userId: user._id,
          ..._.pickBy(
            { ...data, dealership: data.dealership?.id || data.dealership?._id || data.dealership },
            _.identity
          ),
          ..._.pickBy({ isAdmin: data.isAdmin }, _.isBoolean),
        })
        refetch()
      } else {
        await createUser({
          ...data,
          dealership: data.dealership?.id || data.dealership?._id || data.dealership,
        })
      }
      queryClient.refetchQueries(['users'])
      navigate('/admin/users')
    } catch (err) {
      for (const { name, message } of getSubmissionErrors(err)) {
        setError(name, { message })
      }
    }
  }

  useEffect(() => {
    clearErrors('dealership')
  }, [isAdmin.current])

  useEffect(() => {
    if (isHTAStaff?.current) {
      clearErrors('dealership')
      setValue('dealership', null)
      setValue('dealershipBrand', null)
    }
  }, [isHTAStaff.current])

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Box display='flex' flexDirection='column' rowGap={2} justifyContent='space-between'>
        {user?._id && (
          <Box>
            <Typography component='span' variant='body1'>
              User ID:{' '}
            </Typography>
            <Typography component='span' variant='body2'>
              {user._id}
            </Typography>
          </Box>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl>
              <FormControlLabel
                label='Is Admin?'
                control={<Checkbox {...register('isAdmin')} defaultChecked={user?.isAdmin} />}
              />
              {isAdmin.current && (
                <FormHelperText>
                  Allows viewing all data for a selected Dealership Brand.
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          {isAdmin.current && (
            <Grid item xs={12}>
              <FormControl>
                <FormControlLabel
                  label='Is HTA Staff?'
                  control={
                    <Checkbox {...register('isHTAStaff')} defaultChecked={user?.isHTAStaff} />
                  }
                />
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <CustomerForm
            control={control}
            dealershipOptional={isAdmin.current}
            phoneOptional
            user={user}
            onDealershipChange={(event, dealership) => handleChangeDealership(dealership)}
            disableDealership={isAdmin.current && isHTAStaff.current}
            userForm
          />
          {!isHTAStaff?.current && (
            <Grid item xs={12}>
              <AutocompleteElement
                control={control}
                name='dealershipBrand'
                label={`Dealership Brand ${!isAdmin.current && !user?.dealershipBrand ? ' *' : ''}`}
                matchId
                options={dealershipBrandOptions}
                disabled={Boolean(dealership.current)}
                autocompleteProps={{
                  fullWidth: true,
                }}
                rules={{
                  validate: (value) =>
                    !(!isAdmin.current && !user?.dealershipBrand && !value) ||
                    'Dealership brand is required',
                }}
              />
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type='password'
              label='Password'
              inputProps={{
                autoComplete: 'off',
                form: {
                  autocomplete: 'off',
                },
                ...register('password', {
                  required: !user && 'Password is required',
                  minLength: { value: 6, message: 'Password must be 6 or more characters long.' },
                }),
              }}
              required={!user}
              error={Boolean(errors?.['password'])}
              helperText={errors?.['password']?.message}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type='password'
              label='Confirm Password'
              inputProps={{
                autoComplete: 'off',
                form: {
                  autocomplete: 'off',
                },
                ...register('confirmPassword', {
                  validate: (value) => value === password.current || 'Password does not match.',
                }),
              }}
              required={!user}
              error={Boolean(errors?.['confirmPassword'])}
              helperText={errors?.['confirmPassword']?.message}
            />
          </Grid>
        </Grid>

        <Box gap={2} display='flex' justifyContent='flex-end'>
          <Button
            type='button'
            onClick={() => {
              reset()
            }}
            disabled={isSubmitting}
          >
            Reset
          </Button>
          <Button type='submit' variant='contained' disabled={isSubmitting}>
            Submit
          </Button>
        </Box>
      </Box>
    </form>
  )
}

export default UserForm
